"use client"

import React, { useCallback } from "react"
import { useRouter } from "next/router"

import { useOrganization } from "@clerk/nextjs"
import { cn } from "@coval/ui"
import { useMediaQuery } from "usehooks-ts"

import { usePageHeader } from "@/contexts/PageHeaderContext"
import { useSecondarySidebar } from "@/contexts/SecondarySidebarContext"
import { useQueryParams } from "@/hooks/useQueryParams"
import { isDashboardRoute } from "@/utils/isDashboardRoute"

import { useAppShellContributions } from "./AppShellContributionsContext"
import { AutoSecondarySidebar } from "./AutoSecondarySidebar"
import { DefaultPageHeader } from "./DefaultPageHeader"
import {
  EXPANDED_SIDEBAR_WIDTH_CLASS,
  SIDEBAR_COLLAPSE_BREAKPOINT,
} from "./sidebarDimensions"
import { SidebarNavMobileMenu } from "./SidebarNavMobileMenu"
import { SidebarNavPanel } from "./SidebarNavPanel"

interface SidebarNavProps {
  children: React.ReactNode
}

const SidebarNav = ({ children }: SidebarNavProps) => {
  const { customPageHeader, noPaddingBottom, headerBackgroundClassName } =
    usePageHeader()
  // Below this width a docked navigation column would take a share of the
  // viewport the page cannot spare, so the panel moves into the overlay the
  // header's menu button opens and the content region gets the full width.
  const isCompact = useMediaQuery(
    `(max-width: ${SIDEBAR_COLLAPSE_BREAKPOINT}px)`,
  )
  const router = useRouter()
  const { secondarySidebar } = useSecondarySidebar()
  const { organization } = useOrganization()
  const appShellContributions = useAppShellContributions()

  // Dashboard tabs logic
  const isDashboardPage = isDashboardRoute(router.pathname)

  const queryParams = useQueryParams()

  const getHref = useCallback(
    (baseHref: string) => {
      return queryParams.getHref(baseHref, organization?.slug || undefined)
    },
    [queryParams, organization?.slug],
  )

  // A header the page hands over as an empty fragment asks for no header row at
  // all; the shell's own controls then float over the content instead.
  const hasHeaderRow = !(
    customPageHeader !== null &&
    React.isValidElement(customPageHeader) &&
    customPageHeader.type === React.Fragment &&
    !(customPageHeader.props as { children?: unknown }).children
  )

  return (
    <div className="flex h-screen h-dvh">
      {/* Primary Sidebar — docked only where there is room for it. */}
      {!isCompact && (
        <SidebarNavPanel
          className={
            appShellContributions.primaryNavWidthClass ??
            EXPANDED_SIDEBAR_WIDTH_CLASS
          }
        />
      )}

      {/* Secondary Sidebar (context-provided or auto-detected) */}
      {secondarySidebar || <AutoSecondarySidebar />}

      {/* Main Content Area — reflows for the docked Sofia panel. CoviMount
          sets --covi-dock-width / --covi-dock-transition on the document
          root when the panel is docked open; this margin squishes the content
          so the panel sits beside it instead of overlaying it. */}
      <div
        data-app-shell-main-region
        className="relative flex min-w-0 flex-1 flex-col overflow-hidden px-0"
        style={{
          marginRight: "var(--covi-dock-width, 0px)",
          transition: "var(--covi-dock-transition, none)",
        }}
      >
        {/* Inner region reflows for a drawer, which docks between this
            content and Sofia. It is a separate element from the Sofia reflow
            above so each dock keeps its own margin transition and the two
            resize independently: Sofia snaps its width in deliberately, while a
            drawer slides its space open in step with the panel's own 300ms
            slide — except mid-drag, where the Drawer primitive sets
            --drawer-dock-transition to none so the content tracks the pointer
            exactly. */}
        <div
          data-app-shell-content-region
          className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden"
          style={{
            marginRight: "var(--drawer-dock-width, 0px)",
            transition:
              "var(--drawer-dock-transition, margin-right 300ms ease-in-out)",
          }}
        >
          {!hasHeaderRow ? (
            <>
              {isCompact && (
                <SidebarNavMobileMenu className="absolute left-4 top-3 z-30" />
              )}
              {appShellContributions.renderHeaderTrailing?.({
                className: "absolute right-4 top-3 z-30",
              })}
            </>
          ) : (
            <div
              // `items-center` vertically centers the breadcrumb, the trailing
              // Sofia toggle, and every header's right-side actions on the
              // shared `min-h-17` baseline, so they line up with the sidebar's
              // `h-[28px]` selector (whose center also sits at the bar's
              // midline) regardless of each element's intrinsic height. This
              // supersedes the earlier `items-start` + `pt-5` scheme,
              // whose fixed top offset only truly centered content of
              // one specific height and left shorter/taller rows misaligned.
              className={cn(
                // `px-6` is the 24px shell gutter on both sides: the
                // trailing Sofia toggle keeps the same margin from the window
                // edge as the sidebar's left and a table card's right, and the
                // breadcrumb lines up with a table's first column.
                "flex min-w-0 items-center justify-between bg-surface-contrast px-6 pt-2 min-h-17",
                noPaddingBottom ? "pb-0" : "pb-2",
                headerBackgroundClassName,
              )}
            >
              {/* The menu button leads the header row where the panel is not
                  docked, so navigation stays in the top-left corner it occupies
                  at every other width — on the shell gutter the breadcrumb
                  starts from when the panel is docked beside it. */}
              <div className="flex min-w-0 flex-1 items-center gap-2">
                {isCompact && <SidebarNavMobileMenu className="shrink-0" />}
                {customPageHeader !== null
                  ? customPageHeader
                  : (appShellContributions.renderDefaultPageHeader?.({
                      getHref,
                      isDashboardPage,
                    }) ?? <DefaultPageHeader getHref={getHref} />)}
              </div>
              {appShellContributions.renderHeaderTrailing?.({
                className: "ml-2 shrink-0",
              })}
            </div>
          )}

          {appShellContributions.renderStatusBanner?.({
            className: "mx-6 mb-2 shrink-0",
          })}

          <main
            className={cn(
              // The content area is the same contrast surface as the sidebar and
              // the header, so a page's table card reads as a card on it.
              "flex min-h-0 min-w-0 flex-1 flex-col bg-surface-contrast",
              isDashboardPage ? "overflow-hidden" : "overflow-y-auto",
            )}
          >
            {children}
          </main>
        </div>
      </div>
    </div>
  )
}

export default SidebarNav
